import React, { useMemo } from "react";
import type { PipelineTimeSeriesChartProps } from "../../type";
import { useApp } from "../../../../contexts/AppContext";
import {
  MultiDimensionBarChart,
  type MultiDimensionBarRow,
} from "../ChartTypes/MultiDimensionBarChart";
import {
  EMPTY_PIPELINE_TIME_SERIES_MESSAGE,
  MONTH_SHORT,
  PIPELINE_TIME_SERIES_DIMENSIONS,
} from "../../../../utils/consts";
import { multiDimensionBarChartConfig } from "../ChartTypes/styles/multiDimensionBarChartStyles";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const tooltipCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatYAxisAmount = (val: number) => {
  if (val >= 1_000_000) {
    return `$${(val / 1_000_000).toFixed(1)}M`;
  }
  if (val >= 1_000) {
    return `$${(val / 1_000).toFixed(0)}K`;
  }
  return currencyFormatter.format(val);
};

const formatTooltipAmount = (val: number) =>
  tooltipCurrencyFormatter.format(val);

export const PipelineTimeSeriesChart: React.FC<
  PipelineTimeSeriesChartProps
> = ({
  title = "Pipeline - Time Series Analysis",
  loading = false,
  series,
}) => {
  const { drawerOpen } = useApp();

  const rows = useMemo<MultiDimensionBarRow[]>(
    () =>
      series.map((item) => ({
        category: `${MONTH_SHORT[item.month - 1] ?? item.month} ${item.year}`,
        values: {
          wt_pipeline: Number(item.weighted),
          new_pipeline: Number(item.new_pipeline_added),
        },
      })),
    [series],
  );

  return (
    <MultiDimensionBarChart
      title={title}
      loading={loading}
      dimensions={PIPELINE_TIME_SERIES_DIMENSIONS}
      rows={rows}
      height={multiDimensionBarChartConfig.height}
      minHeight={multiDimensionBarChartConfig.minHeight}
      valueSuffix=""
      yAxisValueFormatter={formatYAxisAmount}
      tooltipValueFormatter={formatTooltipAmount}
      xAxisRotateThreshold={multiDimensionBarChartConfig.xAxisRotateThreshold}
      xAxisLabelColor={multiDimensionBarChartConfig.xAxisLabelColor}
      xAxisLabelFontSize={multiDimensionBarChartConfig.xAxisLabelFontSize}
      xAxisLabelFontWeight={multiDimensionBarChartConfig.xAxisLabelFontWeight}
      yAxisLabelColor={multiDimensionBarChartConfig.yAxisLabelColor}
      yAxisLabelFontSize={multiDimensionBarChartConfig.yAxisLabelFontSize}
      emptyMessage={EMPTY_PIPELINE_TIME_SERIES_MESSAGE}
      resizeTrigger={drawerOpen}
    />
  );
};
